import { useMemo } from 'react';

import { useMenuContext } from 'state/menu';

import { ICartEntry } from '../types';
import {
  diagnoseInvalidModalLine,
  formatDaypartsContextLines,
  formatInvalidLineDiagnostic,
} from '../utils/invalid-items-diagnostics';

type InvalidItemsModalDiagnosticsParams = {
  invalidCartEntries: ICartEntry[];
  invalidOfferEntries: ICartEntry[];
  invalidRewardEntries: ICartEntry[];
};

export type InvalidItemsModalDiagnosticRow = {
  key: string;
  lineLabel: string;
  detail: string;
};

export const useInvalidItemsModalDiagnostics = ({
  invalidCartEntries,
  invalidOfferEntries,
  invalidRewardEntries,
}: InvalidItemsModalDiagnosticsParams) => {
  const { getOneMenuItemAvailability, activeDayParts, dayParts } = useMenuContext();

  return useMemo(() => {
    const daypartsContextLines = formatDaypartsContextLines(activeDayParts, dayParts);

    const productRows: InvalidItemsModalDiagnosticRow[] = invalidCartEntries.map(entry => ({
      key: `product-${entry.cartId ?? entry.id}`,
      lineLabel: `${entry.name} (${entry.cartId ?? entry.id})`,
      detail: formatInvalidLineDiagnostic(
        diagnoseInvalidModalLine(entry, getOneMenuItemAvailability, 'product')
      ),
    }));

    const offerRows: InvalidItemsModalDiagnosticRow[] = invalidOfferEntries.map(entry => ({
      key: `offer-${entry.cartId ?? entry.id}`,
      lineLabel: `${entry.name} (${entry.cartId ?? entry.id})`,
      detail: formatInvalidLineDiagnostic(
        diagnoseInvalidModalLine(entry, getOneMenuItemAvailability, 'offer')
      ),
    }));

    const rewardRows: InvalidItemsModalDiagnosticRow[] = invalidRewardEntries.map(entry => ({
      key: `reward-${entry.cartId ?? entry.id}`,
      lineLabel: `${entry.name} (${entry.cartId ?? entry.id})`,
      detail: formatInvalidLineDiagnostic(
        diagnoseInvalidModalLine(entry, getOneMenuItemAvailability, 'reward')
      ),
    }));

    return { productRows, offerRows, rewardRows, daypartsContextLines } as const;
  }, [
    activeDayParts,
    dayParts,
    getOneMenuItemAvailability,
    invalidCartEntries,
    invalidOfferEntries,
    invalidRewardEntries,
  ]);
};
